<div class="admin-page">
  <app-menu [screen]="screen"></app-menu>
  @if (loading) {
    <spinner-overlay [overlay]=true></spinner-overlay>
  }

  <div class="admin-grid">
    <!-- statistics, with what changed on the day chosen -->
    <mat-card appearance="raised" class="panel">
      <div class="panel-header">
        <h2>Statistics</h2>
        <span class="panel-hint">Model changes across all organisations, last 4 weeks</span>
      </div>
      <app-insight-cards [cards]="cards"></app-insight-cards>
      @if (lineData) {
        <p class="chart-hint">Click a day on the chart to see what changed on it.</p>
        <app-line-chart [data]="lineData" [height]="320" [selectable]="true" (pointSelected)="selectDay($event)"></app-line-chart>
      }
      @if (selectedDay) {
        <section class="day" aria-labelledby="day-title" data-testid="admin-day">
          <div class="section-header">
            <h3 id="day-title">{{selectedDayLabel}}</h3>
            <button mat-icon-button type="button" (click)="clearDay()" aria-label="Close the day's changes" matTooltip="Close"><mat-icon>close</mat-icon></button>
          </div>
          @if (dayChanges.length) {
            <p class="summary-line">{{dayChanges.length}} change{{dayChanges.length === 1 ? '' : 's'}} in {{dayOrganisations.length}} organisation{{dayOrganisations.length === 1 ? '' : 's'}}. Choose an organisation to see its usage.</p>
            <div class="chips">
              @for (o of dayOrganisations; track o.organisationKey) {
                <button mat-stroked-button type="button" (click)="selectOrganisationByKey(o.organisationKey)" [matTooltip]="o.people.join(', ')">
                  {{o.organisation}} <span class="chip-count">{{o.changes}}</span>
                </button>
              }
            </div>
            <app-table [columns]="dayColumns" [data]="dayChanges" [filter]="true" [rowSelect]="true" [height]="320" (rowEvent)="selectOrganisationByKey($event.row.organisationKey)"></app-table>
          }@else {
            <p class="summary-line">No model changes were saved on this day.</p>
          }
        </section>
      }
    </mat-card>

    <!-- the latest requests, newest first -->
    <mat-card appearance="raised" class="panel">
      <div class="panel-header">
        <h2>Latest activity</h2>
        <span class="panel-hint">Newest first, since the server last started</span>
        <span class="spacer"></span>
        <button mat-icon-button type="button" (click)="loadActivityData()" aria-label="Refresh the activity" matTooltip="Refresh"><mat-icon>refresh</mat-icon></button>
      </div>
      @if (activityData) {
        <app-table [columns]="activityColumns" [data]="activityData" [filter]="true" [rowSelect]="false" [height]="560"></app-table>
      }
    </mat-card>

    @if (analyticsData) {
      <mat-card appearance="raised" class="panel wide">
        <div class="panel-header">
          <h2>Organisations</h2>
          <span class="panel-hint">Choose one to see how it uses LINQ; double-click to open it</span>
        </div>
        <app-table [columns]="organisationColumns" [data]="analyticsData.organisations" [filter]="true" [rowSelect]="true" [height]="460" (rowEvent)="handleRowEvent($event)" (dblClickEvent)="handleDblClickEvent($event)"></app-table>
      </mat-card>
    }

    <!-- the organisation chosen: how it uses LINQ, for talking to the customer -->
    @if (selectedOrganisation) {
      <mat-card appearance="raised" class="panel wide usage" #usagePanel data-testid="organisation-usage">
        <div class="panel-header">
          <h2>{{selectedOrganisation.name}}</h2>
          @if (usage) {
            <span class="state" [class.warn]="usage.organisation.state !== 'Active'">{{usage.organisation.state}}</span>
            @if (usage.organisation.isPartner) { <span class="state">Partner</span> }
            @if (usage.organisation.isExemptFromPayment) { <span class="state">Exempt</span> }
            @if (usage.organisation.notCustomer) { <span class="state">Not a customer</span> }
          }
          <span class="spacer"></span>
          <button mat-stroked-button type="button" (click)="navigateToOrg(selectedOrganisation)"><mat-icon>open_in_new</mat-icon>Open organisation</button>
          <button mat-icon-button type="button" (click)="closeUsage()" aria-label="Close the organisation's usage" matTooltip="Close"><mat-icon>close</mat-icon></button>
        </div>

        @if (loadingUsage) {
          <div class="loading" role="status"><mat-spinner diameter="22"></mat-spinner><span>Loading its usage…</span></div>
        }@else if (usageError) {
          <p class="error" role="alert">{{usageError}}</p>
        }@else if (usage && summary) {
          <p class="summary-line">
            Created {{usage.organisation.createdDate | date:'d MMM y'}} · last used {{usage.organisation.lastAccess | date:'d MMM y'}}
            @if (usage.organisation.endDate) { · ends {{usage.organisation.endDate | date:'d MMM y'}} }
            @if (summary.lastChange) { · last model change {{summary.lastChange | date:'d MMM y'}} }
          </p>
          <app-insight-cards [cards]="usageCards"></app-insight-cards>

          @if (points.length) {
            <section class="points" aria-labelledby="points-title">
              <h3 id="points-title">Talking points</h3>
              <ul>
                @for (p of points; track p.text) {
                  <li [class]="'point ' + p.tone">
                    <mat-icon aria-hidden="true">{{p.tone === 'good' ? 'thumb_up' : p.tone === 'warn' ? 'priority_high' : 'lightbulb'}}</mat-icon>
                    <span>{{p.text}}</span>
                  </li>
                }
              </ul>
            </section>
          }

          <div class="usage-grid">
            <section>
              <h3>Model changes, last {{usage.days}} days</h3>
              @if (usageLineData) {
                <app-line-chart [data]="usageLineData" [height]="260"></app-line-chart>
              }
            </section>
            <section>
              <h3>Recent changes</h3>
              @if (usageChanges.length) {
                <app-table [columns]="usageChangeColumns" [data]="usageChanges" [filter]="true" [rowSelect]="false" [height]="300"></app-table>
              }@else {
                <p class="summary-line">No model changes in the last {{usage.days}} days.</p>
              }
            </section>
            <section>
              <h3>Models</h3>
              <app-table [columns]="usageModelColumns" [data]="usageModels" [filter]="true" [rowSelect]="false" [height]="300"></app-table>
            </section>
            <section>
              <h3>People</h3>
              <app-table [columns]="usageUserColumns" [data]="usageUsers" [filter]="false" [rowSelect]="false" [height]="300"></app-table>
            </section>
            <section>
              <h3>Latest activity</h3>
              @if (usageActivity.length) {
                <app-table [columns]="usageActivityColumns" [data]="usageActivity" [filter]="true" [rowSelect]="false" [height]="300"></app-table>
              }@else {
                <p class="summary-line">No requests from its people since the server last started.</p>
              }
            </section>
            <section>
              <h3>Projects</h3>
              @if (usage.projects.length) {
                <ul class="projects">
                  @for (p of usage.projects; track p._key) {
                    <li><span class="truncated-name">{{p.name}}</span>
                      @if (p.sandbox) { <span class="state">Sandbox</span> }
                      <span class="muted">started {{p.startDate | date:'d MMM y'}}</span></li>
                  }
                </ul>
              }@else {
                <p class="summary-line">No projects.</p>
              }
            </section>
          </div>

          @if (users && users.length) {
            <section class="manage-users">
              <h3>Manage users</h3>
              <app-users [users]="users"></app-users>
            </section>
          }
        }
      </mat-card>
    }
  </div>
</div>
